// 🔔 Usage Alerts Component
// Displays subscription usage alerts and warnings

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  AlertCircle, 
  XCircle, 
  X, 
  TrendingUp,
  ArrowRight,
  Check
} from 'lucide-react';
import { 
  getUnreadAlerts, 
  markAlertAsRead, 
  dismissAlert,
  ALERT_TYPES
} from '../utils/subscriptionLimits';

/**
 * 🔔 Usage Alert Toast Notification
 */
export const UsageAlertToast = ({ alert, onDismiss, onUpgrade }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    // Auto-dismiss info alerts after 10 seconds
    if (alert.type === ALERT_TYPES.INFO) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [alert]);
  
  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      if (onDismiss) onDismiss(alert.id);
    }, 300);
  };
  
  const getAlertStyle = () => {
    // Check if this is a subscription expiry alert
    const isExpiryAlert = alert.action === 'plan_expiry_reminder' || alert.action === 'subscription_expired';
    
    switch (alert.type) {
      case ALERT_TYPES.BLOCKED:
        return {
          bg: 'bg-red-50',
          border: 'border-red-500',
          text: 'text-red-900',
          icon: <XCircle className="w-6 h-6 text-red-500" />,
          title: isExpiryAlert ? 'Subscription Expired' : 'Limit Reached'
        };
      case ALERT_TYPES.CRITICAL:
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-500',
          text: 'text-orange-900',
          icon: <AlertTriangle className="w-6 h-6 text-orange-500" />,
          title: isExpiryAlert ? 'Subscription Expiring Soon' : 'Critical Warning'
        };
      case ALERT_TYPES.WARNING:
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-500',
          text: 'text-yellow-900',
          icon: <AlertCircle className="w-6 h-6 text-yellow-500" />,
          title: isExpiryAlert ? 'Subscription Reminder' : 'Usage Warning'
        };
      default:
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-500',
          text: 'text-blue-900',
          icon: <AlertCircle className="w-6 h-6 text-blue-500" />,
          title: 'Info'
        };
    }
  };
  
  const style = getAlertStyle();
  
  if (!isVisible) return null;
  
  return (
    <div 
      className={`relative w-full shadow-lg rounded-lg border-l-4 ${style.border} ${style.bg} transform transition-all duration-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {style.icon}
          </div>
          <div className="ml-3 flex-1">
            <h3 className={`text-sm font-semibold ${style.text}`}>
              {style.title}
            </h3>
            <div className={`mt-1 text-sm ${style.text}`}>
              {alert.message}
            </div>
            {alert.featureName && alert.action !== 'plan_expiry_reminder' && alert.action !== 'subscription_expired' && (
              <div className="mt-2 text-xs opacity-75">
                {alert.featureName}: {alert.current}/{alert.limit === 'Unlimited' ? '∞' : alert.limit}
                {alert.percentage && ` (${alert.percentage}%)`}
              </div>
            )}
            {alert.daysRemaining !== undefined && alert.daysRemaining !== null && (
              <div className="mt-2 text-xs opacity-75">
                {alert.daysRemaining > 0 
                  ? `${alert.daysRemaining} day${alert.daysRemaining > 1 ? 's' : ''} remaining`
                  : 'Subscription expired'
                }
              </div>
            )}
            {(alert.type === ALERT_TYPES.BLOCKED || alert.action === 'plan_expiry_reminder' || alert.action === 'subscription_expired') && (
              <div className="mt-3">
                <button
                  onClick={onUpgrade}
                  className="inline-flex items-center px-3 py-2 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 transition-colors"
                >
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {alert.action === 'subscription_expired' || alert.action === 'plan_expiry_reminder' ? 'Renew Plan' : 'Upgrade Plan'}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            )}
          </div>
          <div className="ml-3 flex-shrink-0">
            <button
              onClick={handleDismiss}
              className={`inline-flex rounded-md p-1.5 ${style.text} hover:bg-black hover:bg-opacity-10 transition-colors`}
            >
              <span className="sr-only">Dismiss</span>
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 📊 Usage Alert Banner
 */
export const UsageAlertBanner = ({ alerts, onDismiss, onUpgrade }) => {
  if (!alerts || alerts.length === 0) return null;
  
  // Show the most critical alert
  const criticalAlert = alerts.find(a => a.type === ALERT_TYPES.BLOCKED) ||
                        alerts.find(a => a.type === ALERT_TYPES.CRITICAL) ||
                        alerts[0];
  
  const getAlertStyle = () => {
    switch (criticalAlert.type) {
      case ALERT_TYPES.BLOCKED:
        return {
          bg: 'bg-red-100',
          border: 'border-red-500',
          text: 'text-red-900',
          icon: <XCircle className="w-5 h-5 text-red-500" />
        };
      case ALERT_TYPES.CRITICAL:
        return {
          bg: 'bg-orange-100',
          border: 'border-orange-500',
          text: 'text-orange-900',
          icon: <AlertTriangle className="w-5 h-5 text-orange-500" />
        };
      default:
        return {
          bg: 'bg-yellow-100',
          border: 'border-yellow-500',
          text: 'text-yellow-900',
          icon: <AlertCircle className="w-5 h-5 text-yellow-500" />
        };
    }
  };
  
  const style = getAlertStyle();
  
  return (
    <div className={`border-l-4 ${style.border} ${style.bg} p-4 mb-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          {style.icon}
          <div className="ml-3">
            <p className={`text-sm font-medium ${style.text}`}>
              {criticalAlert.message}
            </p>
            {alerts.length > 1 && (
              <p className="text-xs mt-1 opacity-75">
                +{alerts.length - 1} more alert{alerts.length > 2 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {criticalAlert.type === ALERT_TYPES.BLOCKED && (
            <button
              onClick={onUpgrade}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              Upgrade Plan
            </button>
          )}
          <button
            onClick={() => onDismiss(criticalAlert.id)}
            className="p-1 rounded-md hover:bg-black hover:bg-opacity-10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 🔔 Alert Container (manages multiple alerts)
 */
export const UsageAlertsContainer = ({ companyId, onUpgrade }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadAlerts();
    
    // Reload alerts every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, [companyId]);
  
  const loadAlerts = async () => {
    try {
      if (!companyId) return;
      
      // Import and check expiry reminders
      const { checkAndCreateExpiryReminders } = await import('../utils/subscriptionLimits');
      await checkAndCreateExpiryReminders(companyId);
      
      const unreadAlerts = await getUnreadAlerts(companyId, 10); // Increased to show more alerts
      
      // Sort alerts: blocked first, then critical, then warnings
      const sortedAlerts = unreadAlerts.sort((a, b) => {
        const priority = { blocked: 3, critical: 2, warning: 1, info: 0 };
        return (priority[b.type] || 0) - (priority[a.type] || 0);
      });
      
      setAlerts(sortedAlerts);
      setLoading(false);
    } catch (error) {
      console.error('Error loading alerts:', error);
      setLoading(false);
    }
  };
  
  const handleDismiss = async (alertId) => {
    try {
      await dismissAlert(companyId, alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      console.error('Error dismissing alert:', error);
    }
  };
  
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Default: navigate to pricing page
      window.location.href = '/pricing';
    }
  };
  
  if (loading || alerts.length === 0) return null;
  
  return (
    <div className="usage-alert-stack space-y-2">
      {alerts.map((alert, index) => (
        <UsageAlertToast
          key={alert.id}
          alert={alert}
          onDismiss={handleDismiss}
          onUpgrade={handleUpgrade}
        />
      ))}
    </div>
  );
};

/**
 * 🚫 Limit Reached Modal
 */
export const LimitReachedModal = ({ isOpen, onClose, limitCheck, onUpgrade }) => {
  if (!isOpen || !limitCheck) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Plan Limit Reached
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    You've reached the limit for <strong>{limitCheck.featureName}</strong> on your {limitCheck.planName} plan.
                  </p>
                  <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">Current Usage:</span>
                      <span className="font-semibold text-red-600">
                        {limitCheck.current}/{limitCheck.limit}
                      </span>
                    </div>
                    <div className="mt-2">
                      <div className="bg-white rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-red-500 h-full"
                          style={{ width: `${Math.min(limitCheck.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    To continue using this feature, please upgrade to a higher plan with increased limits.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onUpgrade}
              className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Upgrade Plan
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageAlertsContainer;

